import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import GuideChat from "@/components/guide/GuideChat";
import RichText from "@/components/guide/RichText";
import { GUIDE_MODES, getMode } from "@/components/guide/guideModes";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, FolderOpen, MessageSquare, FileText, ArrowLeft, Pencil, Trash2, Copy, Sparkles,
  Paperclip, Upload, RefreshCw, Eye, Loader2,
} from "lucide-react";

const CATEGORIES = ["Content series", "Digital product", "Book", "Bible study", "Workbook", "Email series", "Website", "App", "Business planning", "General"];
const STATUSES = ["Active", "Waiting", "Completed", "Archived"];
const STATUS_STYLES = {
  Active: "bg-primary/15 text-primary",
  Waiting: "bg-amber-100 text-amber-700",
  Completed: "bg-emerald-100 text-emerald-700",
  Archived: "bg-muted text-muted-foreground",
};
const StatusBadge = ({ status, testid }) => {
  const s = STATUSES.includes(status) ? status : "Active";
  return (
    <span data-testid={testid} className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${STATUS_STYLES[s]}`}>{s}</span>
  );
};

export default function Projects() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // list | detail | chat
  const [active, setActive] = useState(null);
  const [convos, setConvos] = useState([]);
  const [items, setItems] = useState([]);
  const [files, setFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [deleteFileId, setDeleteFileId] = useState(null);
  const uploadInputRef = useRef(null);
  const replaceInputRef = useRef(null);
  const replaceTargetRef = useRef(null);
  const [chat, setChat] = useState({ mode: null, conversationId: null });

  // dialogs
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ title: "", category: "General", description: "", status: "Active" });
  const [editOpen, setEditOpen] = useState(false);
  const [modeOpen, setModeOpen] = useState(false);
  const [itemView, setItemView] = useState(null);
  const [renameConvo, setRenameConvo] = useState(null);
  const [renameText, setRenameText] = useState("");
  const [deleteProjectOpen, setDeleteProjectOpen] = useState(false);
  const [deleteConvoId, setDeleteConvoId] = useState(null);
  const [deleteItemId, setDeleteItemId] = useState(null);

  const loadProjects = useCallback(async () => {
    try { const r = await api.get("/guide/projects"); setProjects(r.data || []); }
    catch (e) { toast.error("Could not load projects."); }
    finally { setLoading(false); }
  }, []);

  const openProject = useCallback(async (id) => {
    try {
      const [p, c, i, f] = await Promise.all([
        api.get(`/guide/projects/${id}`),
        api.get(`/guide/conversations?project_id=${id}`),
        api.get(`/guide/items?project_id=${id}`),
        api.get(`/guide/projects/${id}/files`),
      ]);
      setActive(p.data); setConvos(c.data || []); setItems(i.data || []); setFiles(f.data || []); setView("detail");
    } catch (e) { toast.error("Could not open that project."); }
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  useEffect(() => {
    if (params.get("new") === "1") { setNewOpen(true); setParams({}, { replace: true }); }
    const open = params.get("open");
    if (open) { openProject(open); setParams({}, { replace: true }); }
  }, [params, openProject, setParams]);

  const createProject = async () => {
    if (!form.title.trim()) { toast.error("Give the project a name."); return; }
    try {
      const r = await api.post("/guide/projects", form);
      setNewOpen(false); setForm({ title: "", category: "General", description: "", status: "Active" });
      await loadProjects();
      openProject(r.data.id);
    } catch (e) { toast.error("Could not create project."); }
  };

  const saveEdit = async () => {
    try {
      const r = await api.put(`/guide/projects/${active.id}`, { title: form.title, category: form.category, description: form.description, status: form.status });
      setActive(r.data); setEditOpen(false); loadProjects();
      toast.success("Project updated.");
    } catch (e) { toast.error("Could not update project."); }
  };

  const removeProject = async () => {
    try {
      await api.delete(`/guide/projects/${active.id}`);
      setDeleteProjectOpen(false); setView("list"); setActive(null);
      await loadProjects();
      toast.success("Project removed. Its conversations & drafts were kept (unfiled).");
    } catch (e) { toast.error("Could not remove project."); }
  };

  const startConversation = (key) => { setChat({ mode: getMode(key), conversationId: null }); setModeOpen(false); setView("chat"); };
  const continueConversation = (c) => { setChat({ mode: getMode(c.mode || "ask"), conversationId: c.id }); setView("chat"); };
  const backToDetail = () => { setView("detail"); openProject(active.id); };

  const doRenameConvo = async () => {
    try {
      await api.put(`/guide/conversations/${renameConvo.id}`, { title: renameText.trim() || renameConvo.title });
      setRenameConvo(null); openProject(active.id); toast.success("Renamed.");
    } catch (e) { toast.error("Could not rename."); }
  };

  const removeConvo = async () => {
    try { await api.delete(`/guide/conversations/${deleteConvoId}`); setDeleteConvoId(null); openProject(active.id); toast.success("Conversation removed."); }
    catch (e) { toast.error("Could not remove."); }
  };

  const removeItem = async () => {
    try { await api.delete(`/guide/items/${deleteItemId}`); setDeleteItemId(null); setItemView(null); openProject(active.id); toast.success("Draft removed."); }
    catch (e) { toast.error("Could not remove."); }
  };

  const copyText = async (t) => { try { await navigator.clipboard.writeText(t || ""); toast.success("Copied."); } catch (e) { toast.error("Could not copy."); } };

  // ---------- PROJECT FILES ----------
  const reloadFiles = async () => {
    try { const r = await api.get(`/guide/projects/${active.id}/files`); setFiles(r.data || []); }
    catch (e) { /* keep current list */ }
  };

  const handleUploadChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/guide/projects/${active.id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File added to this project.");
      await reloadFiles();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed. Please try again.");
    } finally { setUploadingFile(false); }
  };

  const handleReplaceChange = async (e) => {
    const file = e.target.files?.[0];
    const fileId = replaceTargetRef.current;
    e.target.value = "";
    if (!file || !fileId) return;
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.put(`/guide/projects/${active.id}/files/${fileId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File replaced.");
      await reloadFiles();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not replace the file.");
    } finally { setUploadingFile(false); replaceTargetRef.current = null; }
  };

  const startReplace = (fileId) => { replaceTargetRef.current = fileId; replaceInputRef.current?.click(); };

  const viewFile = async (f) => {
    try {
      const r = await api.get(`/guide/projects/${active.id}/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("Could not open the file."); }
  };

  const removeFile = async () => {
    try { await api.delete(`/guide/projects/${active.id}/files/${deleteFileId}`); setDeleteFileId(null); await reloadFiles(); toast.success("File removed."); }
    catch (e) { toast.error("Could not remove the file."); }
  };

  const fmtSize = (b) => {
    if (!b && b !== 0) return "";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  };

  // ---------- CHAT VIEW ----------
  if (view === "chat") {
    return (
      <div>
        <GuideChat
          mode={chat.mode}
          conversationId={chat.conversationId}
          projectId={active?.id}
          onConversationCreated={() => {}}
          onBack={backToDetail}
        />
      </div>
    );
  }

  // ---------- DETAIL VIEW ----------
  if (view === "detail" && active) {
    return (
      <div data-testid="project-detail">
        <div className="flex items-start gap-3 mb-8">
          <button onClick={() => { setView("list"); setActive(null); loadProjects(); }} data-testid="project-back" className="p-2 rounded-full hover:bg-muted text-muted-foreground transition-colors mt-1"><ArrowLeft className="h-5 w-5" /></button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-wide px-2 py-0.5 rounded-full bg-accent/20 text-secondary">{active.category}</span>
              <StatusBadge status={active.status} testid="project-detail-status" />
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl mt-2">{active.title}</h1>
            {active.description && <p className="text-muted-foreground mt-2 max-w-2xl leading-relaxed">{active.description}</p>}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={() => { setForm({ title: active.title, category: active.category, description: active.description || "", status: active.status || "Active" }); setEditOpen(true); }} data-testid="project-edit" className="p-2 rounded-full hover:bg-muted text-muted-foreground transition-colors" title="Edit"><Pencil className="h-4 w-4" /></button>
            <button onClick={() => setDeleteProjectOpen(true)} data-testid="project-delete" className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-destructive transition-colors" title="Delete"><Trash2 className="h-4 w-4" /></button>
          </div>
        </div>

        {/* Conversations */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg flex items-center gap-2"><MessageSquare className="h-4 w-4 text-accent" /> Conversations</h2>
          <Button onClick={() => setModeOpen(true)} data-testid="project-new-conversation" className="rounded-full bg-primary hover:bg-primary/90 gap-2"><Plus className="h-4 w-4" /> New conversation</Button>
        </div>
        <Card className="rounded-3xl border-border shadow-soft p-3 mb-8" data-testid="project-conversations">
          {convos.length === 0 ? (
            <p className="text-sm text-muted-foreground px-3 py-6 text-center">No conversations yet. Start one to work with the Guide inside this project.</p>
          ) : (
            <div className="space-y-1">
              {convos.map((c) => (
                <div key={c.id} className="flex items-center gap-2 group">
                  <button onClick={() => continueConversation(c)} data-testid={`project-conversation-${c.id}`} className="flex-1 text-left px-3 py-2.5 rounded-2xl hover:bg-muted transition-colors">
                    <p className="text-sm font-medium truncate">{c.title || "Conversation"}</p>
                    <p className="text-xs text-muted-foreground capitalize">{(c.mode || "ask").replace("_", " ")}</p>
                  </button>
                  <button onClick={() => { setRenameConvo(c); setRenameText(c.title || ""); }} data-testid={`project-rename-conversation-${c.id}`} className="p-2 rounded-full text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity" title="Rename"><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => setDeleteConvoId(c.id)} data-testid={`project-delete-conversation-${c.id}`} className="p-2 rounded-full text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Saved drafts & content */}
        <h2 className="font-heading text-lg flex items-center gap-2 mb-4"><FileText className="h-4 w-4 text-accent" /> Saved drafts & content</h2>
        <Card className="rounded-3xl border-border shadow-soft p-3" data-testid="project-items">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground px-3 py-6 text-center">Anything you Save inside this project's conversations will collect here.</p>
          ) : (
            <div className="space-y-1">
              {items.map((it) => (
                <div key={it.id} className="flex items-center gap-2 group">
                  <button onClick={() => setItemView(it)} data-testid={`project-item-${it.id}`} className="flex-1 flex items-center gap-3 text-left px-3 py-2.5 rounded-2xl hover:bg-muted transition-colors">
                    <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-primary/15 text-primary shrink-0">{it.kind}</span>
                    <span className="text-sm font-medium truncate flex-1">{it.title}</span>
                  </button>
                  <button onClick={() => setDeleteItemId(it.id)} data-testid={`project-delete-item-${it.id}`} className="p-2 rounded-full text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Project files */}
        <div className="flex items-center justify-between mb-4 mt-8">
          <h2 className="font-heading text-lg flex items-center gap-2"><Paperclip className="h-4 w-4 text-accent" /> Files</h2>
          <Button onClick={() => uploadInputRef.current?.click()} disabled={uploadingFile} data-testid="project-upload-file" className="rounded-full bg-primary hover:bg-primary/90 gap-2">
            {uploadingFile ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload file
          </Button>
        </div>
        <input ref={uploadInputRef} type="file" className="hidden" data-testid="project-file-input" onChange={handleUploadChange}
          accept=".pdf,.docx,.xlsx,.xls,.txt,.md,.csv,.json,.png,.jpg,.jpeg,.webp,.gif" />
        <input ref={replaceInputRef} type="file" className="hidden" data-testid="project-file-replace-input" onChange={handleReplaceChange}
          accept=".pdf,.docx,.xlsx,.xls,.txt,.md,.csv,.json,.png,.jpg,.jpeg,.webp,.gif" />
        <Card className="rounded-3xl border-border shadow-soft p-3" data-testid="project-files">
          {files.length === 0 ? (
            <p className="text-sm text-muted-foreground px-3 py-6 text-center">Attach PDFs, Word docs, drafts, images and supporting files. The Guide can read text files you attach here when you work inside this project.</p>
          ) : (
            <div className="space-y-1">
              {files.map((f) => (
                <div key={f.id} className="flex items-center gap-2 group" data-testid={`project-file-${f.id}`}>
                  <button onClick={() => viewFile(f)} data-testid={`project-file-view-${f.id}`} className="flex-1 flex items-center gap-3 text-left px-3 py-2.5 rounded-2xl hover:bg-muted transition-colors">
                    <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-primary/15 text-primary shrink-0">{f.ext}</span>
                    <span className="text-sm font-medium truncate flex-1">{f.original_filename}</span>
                    <span className="text-xs text-muted-foreground shrink-0">{fmtSize(f.size)}</span>
                  </button>
                  <button onClick={() => viewFile(f)} data-testid={`project-file-open-${f.id}`} className="p-2 rounded-full text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity" title="View"><Eye className="h-3.5 w-3.5" /></button>
                  <button onClick={() => startReplace(f.id)} data-testid={`project-file-replace-${f.id}`} className="p-2 rounded-full text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity" title="Replace"><RefreshCw className="h-3.5 w-3.5" /></button>
                  <button onClick={() => setDeleteFileId(f.id)} data-testid={`project-file-delete-${f.id}`} className="p-2 rounded-full text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity" title="Remove"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          )}
        </Card>

        {renderDialogs()}
      </div>
    );
  }

  // ---------- LIST VIEW ----------
  return (
    <div>
      <PageHeader
        testid="projects-header"
        title="Projects"
        subtitle="The home for your active work: content series, products, books, studies, email series, website, app and business planning. The Guide remembers each project's context."
        action={<Button onClick={() => setNewOpen(true)} data-testid="new-project-button" className="rounded-full bg-primary hover:bg-primary/90 gap-2"><Plus className="h-4 w-4" /> New project</Button>}
      />
      {loading ? (
        <div className="flex justify-center py-20"><Sparkles className="h-6 w-6 text-primary animate-pulse" /></div>
      ) : projects.length === 0 ? (
        <Card className="rounded-3xl border-border shadow-soft p-12 text-center" data-testid="projects-empty">
          <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-5"><FolderOpen className="h-7 w-7 text-primary" /></div>
          <h2 className="font-heading text-2xl mb-2">Start your first project</h2>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">Group related conversations and drafts in one place, and let the AI Business Guide pick up where you left off.</p>
          <Button onClick={() => setNewOpen(true)} data-testid="start-project-button" className="rounded-full bg-primary hover:bg-primary/90 gap-2"><Plus className="h-4 w-4" /> New project</Button>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-list">
          {projects.map((p) => (
            <button key={p.id} onClick={() => openProject(p.id)} data-testid={`project-card-${p.id}`} className="text-left">
              <Card className="rounded-3xl border-border shadow-soft p-5 h-full hover:shadow-soft-lg hover:-translate-y-0.5 transition-all duration-300">
                <div className="flex items-center justify-between mb-3">
                  <div className="h-10 w-10 rounded-2xl bg-primary/10 flex items-center justify-center"><FolderOpen className="h-5 w-5 text-primary" /></div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-accent/20 text-secondary">{p.category}</span>
                    <StatusBadge status={p.status} testid={`project-card-status-${p.id}`} />
                  </div>
                </div>
                <p className="font-heading text-lg mb-1 truncate">{p.title}</p>
                {p.description && <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">{p.description}</p>}
                <div className="flex items-center gap-3 mt-4 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" />{p.conversation_count ?? 0} chats</span>
                  <span className="inline-flex items-center gap-1"><FileText className="h-3.5 w-3.5" />{p.item_count ?? 0} drafts</span>
                </div>
              </Card>
            </button>
          ))}
        </div>
      )}
      {renderDialogs()}
    </div>
  );

  function renderDialogs() {
    return (
      <>
        {/* New project */}
        <Dialog open={newOpen} onOpenChange={setNewOpen}>
          <DialogContent className="rounded-3xl sm:max-w-md" data-testid="new-project-dialog">
            <DialogHeader><DialogTitle className="font-heading text-2xl">New project</DialogTitle></DialogHeader>
            <DialogDescription className="sr-only">Create a project to organize related work.</DialogDescription>
            <div className="space-y-4 py-2">
              <div className="space-y-2"><Label>Name</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Q1 launch content" className="rounded-xl" data-testid="new-project-title" /></div>
              <div className="space-y-2"><Label>Type</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="rounded-xl" data-testid="new-project-category"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-2xl">{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="rounded-xl" data-testid="new-project-status"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-2xl">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Description <span className="text-muted-foreground font-normal">(optional)</span></Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What is this project about?" className="rounded-xl min-h-[70px]" data-testid="new-project-description" /></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setNewOpen(false)} className="rounded-full">Cancel</Button>
              <Button onClick={createProject} data-testid="create-project-confirm" className="rounded-full bg-primary hover:bg-primary/90">Create project</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit project */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="rounded-3xl sm:max-w-md" data-testid="edit-project-dialog">
            <DialogHeader><DialogTitle className="font-heading text-2xl">Edit project</DialogTitle></DialogHeader>
            <DialogDescription className="sr-only">Rename or recategorize this project.</DialogDescription>
            <div className="space-y-4 py-2">
              <div className="space-y-2"><Label>Name</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl" data-testid="edit-project-title" /></div>
              <div className="space-y-2"><Label>Type</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-2xl">{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="rounded-xl" data-testid="edit-project-status"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-2xl">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl min-h-[70px]" /></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setEditOpen(false)} className="rounded-full">Cancel</Button>
              <Button onClick={saveEdit} data-testid="save-project-edit" className="rounded-full bg-primary hover:bg-primary/90">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Pick a mode for new conversation */}
        <Dialog open={modeOpen} onOpenChange={setModeOpen}>
          <DialogContent className="rounded-3xl sm:max-w-lg" data-testid="project-mode-dialog">
            <DialogHeader><DialogTitle className="font-heading text-2xl">Start a conversation</DialogTitle></DialogHeader>
            <DialogDescription>Choose how the Guide should help with this project.</DialogDescription>
            <div className="grid sm:grid-cols-2 gap-2 py-2">
              {GUIDE_MODES.map((m) => {
                const Icon = m.icon;
                return (
                  <button key={m.key} onClick={() => startConversation(m.key)} data-testid={`project-mode-${m.key}`} className="flex items-start gap-3 text-left px-4 py-3 rounded-2xl bg-muted/50 hover:bg-muted transition-colors">
                    <Icon className="h-5 w-5 text-primary mt-0.5 shrink-0" strokeWidth={1.75} />
                    <div><p className="text-sm font-medium">{m.label}</p><p className="text-xs text-muted-foreground line-clamp-2">{m.blurb}</p></div>
                  </button>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>

        {/* Rename conversation */}
        <Dialog open={!!renameConvo} onOpenChange={(o) => !o && setRenameConvo(null)}>
          <DialogContent className="rounded-3xl sm:max-w-sm" data-testid="rename-conversation-dialog">
            <DialogHeader><DialogTitle className="font-heading text-xl">Rename conversation</DialogTitle></DialogHeader>
            <DialogDescription className="sr-only">Give this conversation a clearer name.</DialogDescription>
            <Input value={renameText} onChange={(e) => setRenameText(e.target.value)} className="rounded-xl" data-testid="rename-conversation-input" />
            <DialogFooter>
              <Button variant="ghost" onClick={() => setRenameConvo(null)} className="rounded-full">Cancel</Button>
              <Button onClick={doRenameConvo} data-testid="rename-conversation-save" className="rounded-full bg-primary hover:bg-primary/90">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Item viewer */}
        <Dialog open={!!itemView} onOpenChange={(o) => !o && setItemView(null)}>
          <DialogContent className="rounded-3xl sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="project-item-dialog">
            <DialogHeader><DialogTitle className="font-heading text-2xl pr-8">{itemView?.title}</DialogTitle></DialogHeader>
            <DialogDescription className="capitalize">{itemView?.kind} · from {(itemView?.mode || "ask").replace("_", " ")}</DialogDescription>
            <div className="overflow-y-auto rounded-2xl bg-muted/30 p-5 flex-1" data-testid="project-item-content"><RichText text={itemView?.content} /></div>
            <DialogFooter className="flex-row justify-end gap-2">
              <Button variant="outline" onClick={() => copyText(itemView?.content)} data-testid="project-item-copy" className="rounded-full border-border gap-1.5"><Copy className="h-4 w-4" /> Copy</Button>
              <Button variant="outline" onClick={() => setDeleteItemId(itemView?.id)} data-testid="project-item-delete" className="rounded-full border-border gap-1.5 text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /> Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <AlertDialog open={deleteProjectOpen} onOpenChange={setDeleteProjectOpen}>
          <AlertDialogContent className="rounded-3xl">
            <AlertDialogHeader><AlertDialogTitle className="font-heading">Remove this project?</AlertDialogTitle>
              <AlertDialogDescription>The project will be removed. Its conversations and drafts are kept (they become unfiled), not deleted.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel className="rounded-full">Keep it</AlertDialogCancel>
              <AlertDialogAction onClick={removeProject} data-testid="confirm-delete-project" className="rounded-full bg-destructive hover:bg-destructive/90">Remove</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={!!deleteConvoId} onOpenChange={(o) => !o && setDeleteConvoId(null)}>
          <AlertDialogContent className="rounded-3xl">
            <AlertDialogHeader><AlertDialogTitle className="font-heading">Remove this conversation?</AlertDialogTitle>
              <AlertDialogDescription>This conversation will be permanently removed.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel className="rounded-full">Keep it</AlertDialogCancel>
              <AlertDialogAction onClick={removeConvo} data-testid="confirm-delete-project-conversation" className="rounded-full bg-destructive hover:bg-destructive/90">Remove</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={!!deleteItemId} onOpenChange={(o) => !o && setDeleteItemId(null)}>
          <AlertDialogContent className="rounded-3xl">
            <AlertDialogHeader><AlertDialogTitle className="font-heading">Remove this draft?</AlertDialogTitle>
              <AlertDialogDescription>This saved draft will be permanently removed.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel className="rounded-full">Keep it</AlertDialogCancel>
              <AlertDialogAction onClick={removeItem} data-testid="confirm-delete-item" className="rounded-full bg-destructive hover:bg-destructive/90">Remove</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={!!deleteFileId} onOpenChange={(o) => !o && setDeleteFileId(null)}>
          <AlertDialogContent className="rounded-3xl">
            <AlertDialogHeader><AlertDialogTitle className="font-heading">Remove this file?</AlertDialogTitle>
              <AlertDialogDescription>This file will be permanently removed from the project.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel className="rounded-full">Keep it</AlertDialogCancel>
              <AlertDialogAction onClick={removeFile} data-testid="confirm-delete-file" className="rounded-full bg-destructive hover:bg-destructive/90">Remove</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }
}
